import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { Sparkles } from 'lucide-react';
import { suggestQueries } from '@/lib/gemini';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

interface Props {
  query: string;
  lang: 'en' | 'fa';
  onPick: (suggestion: string) => void;
}

/**
 * Inline Gemini-powered query refinement block for the command palette.
 *
 * Renders nothing until the user has typed at least 2 characters, then
 * debounces for 400ms before firing a client-side Gemini Flash call
 * via Firebase AI Logic. Results are cached for 30s so moving the
 * arrow keys through the list doesn't re-trigger.
 */
export function CommandPaletteSuggestions({ query, lang, onPick }: Props) {
  const [debounced, setDebounced] = useState(query);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 400);
    return () => clearTimeout(id);
  }, [query]);

  const enabled = debounced.trim().length >= 2;

  const { data, isFetching } = useQuery({
    queryKey: ['gemini', 'suggest', debounced, lang],
    queryFn: () => suggestQueries(debounced, lang),
    enabled,
    staleTime: 30_000,
    retry: 0,
  });

  if (query.trim().length < 2) return null;

  const suggestions = data ?? [];

  if (!isFetching && suggestions.length === 0) return null;

  return (
    <Command.Group heading={t('palette.ai-suggestions', lang)}>
      {isFetching && suggestions.length === 0 && (
        <div className="flex items-center gap-2 px-2 py-2 text-xs text-[var(--color-ink-muted)]">
          <Sparkles className="size-4 animate-pulse" />
          <span>{t('palette.ai-thinking', lang)}</span>
        </div>
      )}
      {suggestions.map((s) => (
        <Command.Item
          key={s}
          value={`ai:${s}`}
          onSelect={() => onPick(s)}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-xs px-2 py-2 text-sm',
            'data-[selected=true]:bg-[var(--color-accent-soft)] data-[selected=true]:text-[var(--color-ink)]',
          )}
        >
          <span className="text-[var(--color-ink-muted)]">
            <Sparkles className="size-4" />
          </span>
          <span className="flex-1 truncate">{s}</span>
        </Command.Item>
      ))}
    </Command.Group>
  );
}
